import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { hrApi } from '@/api';
import { PageActionGate } from '@/components/auth/PageActionGate';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column, TableRowActions } from '@/components/ui/DataTable';
import { DatePicker } from '@/components/ui/DatePicker';
import { FormGrid } from '@/components/ui/FormGrid';
import { FormActions } from '@/components/ui/FormActions';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { HrHolidayTypesSection } from '@/modules/hr/components/HrHolidayTypesSection';
import { HrScheduleCalendarLegend } from '@/modules/hr/components/HrScheduleCalendarLegend';
import type { HrCompanyHoliday, HrHolidayType } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { cn } from '@/utils/cn';

type Tab = 'calendar' | 'types';

export function HrHolidaysPage() {
  const currentYear = new Date().getFullYear();
  const [tab, setTab] = useState<Tab>('calendar');
  const [year, setYear] = useState(String(currentYear));
  const [holidays, setHolidays] = useState<HrCompanyHoliday[]>([]);
  const [holidayTypes, setHolidayTypes] = useState<HrHolidayType[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editId, setEditId] = useState<number | null>(null);
  const [holidayDate, setHolidayDate] = useState('');
  const [holidayName, setHolidayName] = useState('');
  const [holidayType, setHolidayType] = useState('');

  const range = useMemo(() => {
    const y = Number(year) || currentYear;
    return { start: `${y}-01-01`, end: `${y}-12-31` };
  }, [currentYear, year]);

  const typeOptions = useMemo(
    () =>
      holidayTypes
        .filter((t) => t.active)
        .map((t) => ({ value: t.code, label: t.name })),
    [holidayTypes],
  );

  const loadTypes = useCallback(async () => {
    try {
      const rows = await hrApi.listHolidayTypes(false);
      setHolidayTypes(rows);
      setHolidayType((prev) => prev || rows[0]?.code || '');
    } catch (err) {
      showApiError(err, 'Failed to load holiday types');
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await hrApi.listCompanyHolidays(range.start, range.end);
      setHolidays(rows);
    } catch (err) {
      showApiError(err, 'Failed to load holidays');
    } finally {
      setLoading(false);
    }
  }, [range.end, range.start]);

  useEffect(() => {
    void loadTypes();
  }, [loadTypes]);

  useEffect(() => {
    if (tab === 'calendar') void load();
  }, [tab, load]);

  const resetForm = () => {
    setEditId(null);
    setHolidayDate('');
    setHolidayName('');
    setHolidayType(typeOptions[0]?.value ?? '');
  };

  const startEdit = (row: HrCompanyHoliday) => {
    if (!row.id) return;
    setEditId(row.id);
    setHolidayDate(row.holidayDate.slice(0, 10));
    setHolidayName(row.holidayName);
    setHolidayType(row.holidayType ?? typeOptions[0]?.value ?? '');
  };

  const saveHoliday = async () => {
    if (!holidayDate || !holidayName.trim()) {
      notify.error('Date and name are required');
      return;
    }
    if (!holidayType) {
      notify.error('Select a holiday type — configure types on the Holiday types tab first');
      setTab('types');
      return;
    }
    setSaving(true);
    try {
      const body = {
        holidayDate,
        holidayName: holidayName.trim(),
        holidayType,
      };
      if (editId != null) {
        await hrApi.updateHoliday(editId, body);
        notify.success('Holiday updated');
      } else {
        await hrApi.createHoliday(body);
        notify.success('Holiday added');
      }
      resetForm();
      await load();
    } catch (err) {
      showApiError(err, editId != null ? 'Update failed' : 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const removeHoliday = async (id: number) => {
    try {
      await hrApi.deleteHoliday(id);
      notify.success('Holiday removed');
      if (editId === id) resetForm();
      await load();
    } catch (err) {
      showApiError(err, 'Delete failed');
    }
  };

  const columns: Column<HrCompanyHoliday>[] = [
    { key: 'holidayDate', header: 'Date' },
    { key: 'holidayName', header: 'Name' },
    {
      key: 'holidayType',
      header: 'Type',
      render: (r) => r.holidayTypeName ?? r.holidayType ?? '—',
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) =>
        row.id ? (
          <TableRowActions>
            <PageActionGate action="edit">
              <Button variant="secondary" size="sm" onClick={() => startEdit(row)}>
                Edit
              </Button>
            </PageActionGate>
            <PageActionGate action="delete">
              <Button variant="ghost" size="sm" onClick={() => void removeHoliday(row.id!)}>
                Delete
              </Button>
            </PageActionGate>
          </TableRowActions>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Public holidays"
        description="Company calendar days and customizable holiday categories for attendance, leave, and schedules"
        breadcrumbs={[
          { label: 'HR', to: '/hr' },
          { label: 'Setup', to: '/hr/setup' },
          { label: 'Public holidays' },
        ]}
        actions={
          <Link
            to="/org/companies"
            className="inline-flex items-center rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
          >
            Company work calendar
          </Link>
        }
      />

      <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 dark:border-slate-700 dark:bg-slate-900/40">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Holidays apply to the <strong>active company</strong> in the header switcher. Configure weekly working days
          under Organization → Companies → Calendar. Define your own holiday types on the <strong>Holiday types</strong>{' '}
          tab (or per company in the company calendar dialog).
        </p>
        <HrScheduleCalendarLegend className="mt-3" />
      </div>

      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700">
        {(
          [
            { id: 'calendar' as const, label: 'Holiday calendar' },
            { id: 'types' as const, label: 'Holiday types' },
          ] as const
        ).map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={cn(
              'border-b-2 px-4 py-2 text-sm font-medium transition-colors',
              tab === item.id
                ? 'border-primary-600 text-primary-700 dark:text-primary-300'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'types' ? (
        <section className="card p-4">
          <HrHolidayTypesSection onTypesChange={(rows) => setHolidayTypes(rows)} />
        </section>
      ) : (
        <section className="card space-y-4 p-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <Input
              label="Year"
              type="number"
              className="max-w-[8rem]"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              min={2000}
              max={2100}
            />
            <span className="text-sm text-slate-500">
              {holidays.length} holiday(s) in {year}
            </span>
          </div>

          {typeOptions.length === 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
              No active holiday types for this company.{' '}
              <button type="button" className="font-medium underline" onClick={() => setTab('types')}>
                Add holiday types
              </button>{' '}
              before creating calendar entries.
            </div>
          ) : null}

          <PageActionGate action="create">
            <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
              <h3 className="mb-3 text-sm font-medium">{editId != null ? 'Edit holiday' : 'Add holiday'}</h3>
              <FormGrid columns={4} className="items-end">
                <DatePicker label="Date" value={holidayDate} onChange={setHolidayDate} />
                <Input label="Name" value={holidayName} onChange={(e) => setHolidayName(e.target.value)} />
                <Select
                  label="Type"
                  value={holidayType}
                  onChange={setHolidayType}
                  options={typeOptions}
                  placeholder="Select type"
                />
              </FormGrid>
              <FormActions
                primaryLabel={editId != null ? 'Save changes' : 'Add holiday'}
                primaryLoading={saving}
                onPrimary={() => void saveHoliday()}
                onCancel={editId != null ? resetForm : undefined}
                onReset={editId == null ? resetForm : undefined}
              />
            </div>
          </PageActionGate>

          <DataTable
            columns={columns}
            data={holidays}
            loading={loading}
            keyField="id"
            emptyTitle="No holidays for this year"
            emptyDescription="Add holidays so leave and attendance respect your company calendar."
          />
        </section>
      )}
    </div>
  );
}
